import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Event } from '../types';
import './Events.css';

const Events: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [activeTab, setActiveTab] = useState<'upcoming' | 'past'>('upcoming');
  const [selectedImage, setSelectedImage] = useState<{ url: string; caption: string } | null>(null);

  const agendaItems = [
    { time: '09:30-09:40', titleKey: 'events.summer2026.agenda.welcome' },
    { time: '09:40-10:30', titleKey: 'events.summer2026.agenda.reflections' },
    { time: '10:30-11:00', titleKey: 'events.summer2026.agenda.coffee' },
    { time: '11:00-13:00', titleKey: 'events.summer2026.agenda.roundtable' },
    { time: '13:00-14:15', titleKey: 'events.summer2026.agenda.lunch' },
    { time: '14:20-14:30', titleKey: 'events.summer2026.agenda.walk' },
    { time: '14:46-14:55', titleKey: 'events.summer2026.agenda.silence' },
    { time: '14:55-15:10', titleKey: 'events.summer2026.agenda.choral' },
    { time: '15:10', titleKey: 'events.summer2026.agenda.closing' }
  ];

  const pastEvents: Event[] = [
    {
      id: '1',
      title: t('events.past.secondEdition.title'),
      date: '2025-11-05',
      location: t('events.past.secondEdition.location'),
      description: t('events.past.secondEdition.description'),
      image: '/Peja Peace Forum 2nd Edition/Peja Peace Forum 2nd Edition.jpg',
      isUpcoming: false
    },
    {
      id: '4',
      title: t('events.past.firstEdition.title'),
      date: '2024-09-11',
      location: t('events.past.firstEdition.location'),
      description: t('events.past.firstEdition.description'),
      image: '/Peja Peace Forum 1st Edition.jpg',
      isUpcoming: false
    }
  ];

  const currentEvents = pastEvents;
  const locale = i18n.language?.startsWith('sq') ? 'sq-AL' : 'en-US';

  return (
    <div className="events">
      <section className="events-hero section">
        <div className="container">
          <div className="events-hero-content text-center">
            <h1>{t('events.hero.title')}</h1>
            <p className="hero-subtitle">{t('events.hero.subtitle')}</p>
          </div>
        </div>
      </section>

      <section className="events-navigation">
        <div className="container">
          <div className="events-tabs">
            <button
              className={`tab-button ${activeTab === 'upcoming' ? 'active' : ''}`}
              onClick={() => setActiveTab('upcoming')}
            >
              {t('events.tabs.upcoming')}
            </button>
            <button
              className={`tab-button ${activeTab === 'past' ? 'active' : ''}`}
              onClick={() => setActiveTab('past')}
            >
              {t('events.tabs.past')}
            </button>
          </div>
        </div>
      </section>

      {activeTab === 'upcoming' && (
        <section className="summer-meeting section">
          <div className="container">
            <div className="summer-meeting-header">
              <p className="summer-meeting-motto">{t('events.summer2026.motto')}</p>
              <h2>{t('events.summer2026.title')}</h2>
              <p className="summer-meeting-tagline">{t('events.summer2026.tagline')}</p>
              <div className="summer-meeting-meta">
                <span>{t('events.summer2026.date')}</span>
                <span>{t('events.summer2026.location')}</span>
              </div>
            </div>

            <p className="summer-meeting-description">{t('events.summer2026.description')}</p>

            <div className="summer-meeting-highlights">
              <div className="summer-highlight-card">
                <h3>{t('events.summer2026.roundtable.title')}</h3>
                <p className="summer-highlight-theme">"{t('events.summer2026.roundtable.theme')}"</p>
                <p>{t('events.summer2026.roundtable.description')}</p>
              </div>
              <div className="summer-highlight-card">
                <h3>{t('events.summer2026.ceremony.title')}</h3>
                <p className="summer-highlight-theme">{t('events.summer2026.ceremony.time')}</p>
                <p>{t('events.summer2026.ceremony.description')}</p>
              </div>
            </div>

            <div className="summer-meeting-partners">
              <h3>{t('events.summer2026.partnersTitle')}</h3>
              <ul>
                <li>{t('events.summer2026.partners.centre')}</li>
                <li>{t('events.summer2026.partners.rotary')}</li>
                <li>{t('events.summer2026.partners.municipality')}</li>
              </ul>
            </div>

            <div className="summer-meeting-agenda">
              <h3>{t('events.summer2026.agendaTitle')}</h3>
              <div className="agenda-list">
                {agendaItems.map((item) => (
                  <div key={item.time} className="agenda-item">
                    <span className="agenda-time">{item.time}</span>
                    <span className="agenda-title">{t(item.titleKey)}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="summer-meeting-documents">
              <h3>{t('events.summer2026.documentsTitle')}</h3>
              <div className="documents-grid">
                <button
                  type="button"
                  className="document-card"
                  onClick={() =>
                    setSelectedImage({
                      url: '/events/summer-meeting-2026-invitation.jpg',
                      caption: t('events.summer2026.invitationCaption')
                    })
                  }
                >
                  <img
                    src="/events/summer-meeting-2026-invitation.jpg"
                    alt={t('events.summer2026.invitationCaption')}
                  />
                  <span>{t('events.summer2026.invitationCaption')}</span>
                </button>
                <button
                  type="button"
                  className="document-card"
                  onClick={() =>
                    setSelectedImage({
                      url: '/events/summer-meeting-2026-agenda.jpg',
                      caption: t('events.summer2026.agendaCaption')
                    })
                  }
                >
                  <img
                    src="/events/summer-meeting-2026-agenda.jpg"
                    alt={t('events.summer2026.agendaCaption')}
                  />
                  <span>{t('events.summer2026.agendaCaption')}</span>
                </button>
              </div>
            </div>
          </div>
        </section>
      )}

      {activeTab === 'past' && (
        <section className="events-list section">
          <div className="container">
            <div className="events-grid">
              {currentEvents.map((event) => (
                <div key={event.id} className="event-card">
                  <div className="event-image">
                    <img
                      src={event.image}
                      alt={event.title}
                      onError={(e) => {
                        e.currentTarget.src = '/images/placeholder-event.jpg';
                      }}
                    />
                    <div className="event-date-badge">
                      <div className="date-day">{new Date(event.date).getDate()}</div>
                      <div className="date-month">
                        {new Date(event.date).toLocaleDateString(locale, { month: 'short' })}
                      </div>
                    </div>
                  </div>
                  <div className="event-content">
                    <h3 className="event-title">{event.title}</h3>
                    <div className="event-meta">
                      <span className="event-location">{event.location}</span>
                      <span className="event-date">
                        {new Date(event.date).toLocaleDateString(locale, {
                          weekday: 'long',
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })}
                      </span>
                    </div>
                    <p className="event-description">{event.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {selectedImage && (
        <div className="image-modal" onClick={() => setSelectedImage(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setSelectedImage(null)}>
              &times;
            </button>
            <img src={selectedImage.url} alt={selectedImage.caption} />
            <div className="modal-caption">{selectedImage.caption}</div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Events;
